<script lang="ts">
  import { onDestroy } from 'svelte';
  import { liveInvitesByEventStore, liveEventGuestsStore } from '../../lib/convexRealtime';
  import {
    bulkEnsurePersonalInvites,
    createPersonalInvite,
    deleteInvite,
    fetchPersonalInvitesExport,
    regenerateInvite,
    revokeInvite
  } from '../../lib/convexApi';
  import type { EventGuestEntry, InviteRecord } from '../../lib/types';
  import Notice from '../ui/Notice.svelte';
  import LoadingBlock from '../ui/LoadingBlock.svelte';

  export let eventId: string;
  export let eventName: string;

  let inviteStore: ReturnType<typeof liveInvitesByEventStore> | null = null;
  let guestStore: ReturnType<typeof liveEventGuestsStore> | null = null;
  let inviteUnsub: (() => void) | null = null;
  let guestUnsub: (() => void) | null = null;
  let subscribedId: string | null = null;

  let invites: InviteRecord[] = [];
  let guests: EventGuestEntry[] = [];
  let loading = true;
  let error: string | null = null;

  let actionCode: string | null = null;
  let actionError: string | null = null;
  let notice: string | null = null;
  let bulkPending = false;
  let createTicketId = '';
  let creating = false;
  let copiedCode: string | null = null;

  $: if (eventId && eventId !== subscribedId) {
    subscribedId = eventId;
    notice = null;
    actionError = null;
    inviteUnsub?.();
    inviteStore?.dispose();
    guestUnsub?.();
    guestStore?.dispose();
    inviteStore = liveInvitesByEventStore(eventId);
    inviteUnsub = inviteStore.subscribe(state => {
      invites = state.data ?? [];
      loading = state.loading;
      error = state.error;
    });
    guestStore = liveEventGuestsStore(eventId);
    guestUnsub = guestStore.subscribe(state => {
      guests = state.data ?? [];
    });
  }

  onDestroy(() => {
    inviteUnsub?.();
    inviteStore?.dispose();
    guestUnsub?.();
    guestStore?.dispose();
  });

  $: personalInvites = invites.filter(invite => invite.kind === 'personal');
  $: plusOneInvites = invites.filter(invite => invite.kind !== 'personal');

  $: guestNameByTicket = new Map(guests.map(guest => [String(guest.ticketId), guest.person?.name ?? 'Guest']));
  $: guestNameByPerson = new Map(
    guests.filter(guest => guest.person).map(guest => [String(guest.person!.id), guest.person!.name])
  );

  function inviteLink(code: string): string {
    return `${window.location.origin}/?invite=${encodeURIComponent(code)}`;
  }

  function inviteTargetName(invite: InviteRecord): string {
    if (invite.targetPersonId && guestNameByPerson.has(String(invite.targetPersonId))) {
      return guestNameByPerson.get(String(invite.targetPersonId)) ?? 'Guest';
    }
    if (invite.targetTicketId && guestNameByTicket.has(String(invite.targetTicketId))) {
      return guestNameByTicket.get(String(invite.targetTicketId)) ?? 'Guest';
    }
    return invite.targetNormalizedEmail ?? '—';
  }

  function inviterName(invite: InviteRecord): string {
    if (invite.inviterTicketId && guestNameByTicket.has(String(invite.inviterTicketId))) {
      return guestNameByTicket.get(String(invite.inviterTicketId)) ?? '—';
    }
    return '—';
  }

  async function copy(code: string, link = false) {
    try {
      await navigator.clipboard.writeText(link ? inviteLink(code) : code);
      copiedCode = `${code}${link ? ':link' : ''}`;
      setTimeout(() => {
        if (copiedCode === `${code}${link ? ':link' : ''}`) copiedCode = null;
      }, 1500);
    } catch {
      actionError = 'Could not copy to the clipboard.';
    }
  }

  async function run(code: string | null, action: () => Promise<unknown>) {
    actionCode = code;
    actionError = null;
    try {
      await action();
      await inviteStore?.refresh();
    } catch (err) {
      actionError = err instanceof Error ? err.message : 'Action failed.';
    } finally {
      actionCode = null;
    }
  }

  async function bulkEnsure() {
    bulkPending = true;
    actionError = null;
    notice = null;
    try {
      const result = await bulkEnsurePersonalInvites(eventId);
      if (result) {
        notice = `Personal invites: ${result.created} created, ${result.keptActive} already active, ${result.replacedRevoked} replaced, ${result.skipped} skipped.`;
      }
      await inviteStore?.refresh();
    } catch (err) {
      actionError = err instanceof Error ? err.message : 'Bulk generation failed.';
    } finally {
      bulkPending = false;
    }
  }

  async function createForGuest() {
    if (!createTicketId) return;
    creating = true;
    actionError = null;
    try {
      const invite = await createPersonalInvite({ eventId, targetTicketId: createTicketId });
      if (!invite) throw new Error('Invite could not be created.');
      createTicketId = '';
      await inviteStore?.refresh();
    } catch (err) {
      actionError = err instanceof Error ? err.message : 'Could not create the invite.';
    } finally {
      creating = false;
    }
  }

  function csvEscape(value: string): string {
    return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
  }

  async function exportPersonal() {
    actionError = null;
    try {
      const rows = await fetchPersonalInvitesExport(eventId);
      const header = ['Guest', 'Email', 'Phone', 'Ticket code', 'Invite code', 'Invite link', 'Status', 'Used'];
      const csv = [
        header,
        ...rows.map(row => [
          row.guestName,
          row.guestEmail ?? '',
          row.guestPhone ?? '',
          row.ticketCode ?? '',
          row.code,
          inviteLink(row.code),
          row.status,
          String(row.usedCount)
        ])
      ]
        .map(row => row.map(csvEscape).join(','))
        .join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${eventId}-invites.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      actionError = err instanceof Error ? err.message : 'Export failed.';
    }
  }

  function statusLabel(invite: InviteRecord): string {
    if (invite.status === 'revoked') return 'Revoked';
    if (invite.maxUses > 0 && invite.usedCount >= invite.maxUses) return 'Used';
    return 'Active';
  }
</script>

<section class="invites surface surface--sunken" aria-label={`Invites for ${eventName}`}>
  <header class="invites__header">
    <h4>Invites · {eventName}</h4>
    <div class="invites__tools">
      <button type="button" class="secondary" on:click={bulkEnsure} disabled={bulkPending}>
        {bulkPending ? 'Generating…' : 'Generate missing personal invites'}
      </button>
      <button type="button" class="ghost" on:click={exportPersonal} disabled={personalInvites.length === 0}>
        Export CSV
      </button>
    </div>
  </header>

  {#if actionError}
    <Notice variant="error">{actionError}</Notice>
  {/if}
  {#if notice}
    <Notice>{notice}</Notice>
  {/if}

  <form class="invites__create" on:submit|preventDefault={createForGuest}>
    <select bind:value={createTicketId}>
      <option value="">Create personal invite for…</option>
      {#each guests as guest (guest.ticketId)}
        <option value={guest.ticketId}>{guest.person?.name ?? guest.code}</option>
      {/each}
    </select>
    <button type="submit" class="accent" disabled={!createTicketId || creating}>
      {creating ? 'Creating…' : 'Create invite'}
    </button>
  </form>

  {#if loading && invites.length === 0}
    <LoadingBlock label="Loading invites…" />
  {:else if error}
    <Notice variant="error">Could not load invites: {error}</Notice>
  {:else}
    <div class="invites__group">
      <h5>Personal invites ({personalInvites.length})</h5>
      {#if personalInvites.length === 0}
        <p class="invites__empty">None yet — generate them for the whole guest list above.</p>
      {:else}
        <ul>
          {#each personalInvites as invite (invite.id)}
            <li class="invites__row" class:invites__row--revoked={invite.status === 'revoked'}>
              <div class="invites__info">
                <span class="invites__code">{invite.code}</span>
                <span class="invites__meta">
                  {inviteTargetName(invite)} · {statusLabel(invite)} · used {invite.usedCount}/{invite.maxUses}
                </span>
              </div>
              <div class="invites__actions">
                <button type="button" class="ghost" on:click={() => copy(invite.code, true)}>
                  {copiedCode === `${invite.code}:link` ? 'Copied!' : 'Copy link'}
                </button>
                {#if invite.status !== 'revoked'}
                  <button type="button" class="ghost" disabled={actionCode === invite.code} on:click={() => run(invite.code, () => regenerateInvite(invite.code))}>
                    Regenerate
                  </button>
                  <button type="button" class="ghost" disabled={actionCode === invite.code} on:click={() => run(invite.code, () => revokeInvite(invite.code))}>
                    Revoke
                  </button>
                {:else}
                  <button type="button" class="ghost" disabled={actionCode === invite.code} on:click={() => run(invite.code, () => deleteInvite(invite.code))}>
                    Delete
                  </button>
                {/if}
              </div>
            </li>
          {/each}
        </ul>
      {/if}
    </div>

    <div class="invites__group">
      <h5>Plus-one invites ({plusOneInvites.length})</h5>
      {#if plusOneInvites.length === 0}
        <p class="invites__empty">No plus-one invites issued by guests yet.</p>
      {:else}
        <ul>
          {#each plusOneInvites as invite (invite.id)}
            <li class="invites__row" class:invites__row--revoked={invite.status === 'revoked'}>
              <div class="invites__info">
                <span class="invites__code">{invite.code}</span>
                <span class="invites__meta">
                  from {inviterName(invite)} · {statusLabel(invite)} · used {invite.usedCount}/{invite.maxUses}
                </span>
              </div>
              <div class="invites__actions">
                <button type="button" class="ghost" on:click={() => copy(invite.code, true)}>
                  {copiedCode === `${invite.code}:link` ? 'Copied!' : 'Copy link'}
                </button>
                {#if invite.status !== 'revoked'}
                  <button type="button" class="ghost" disabled={actionCode === invite.code} on:click={() => run(invite.code, () => revokeInvite(invite.code))}>
                    Revoke
                  </button>
                {:else}
                  <button type="button" class="ghost" disabled={actionCode === invite.code} on:click={() => run(invite.code, () => deleteInvite(invite.code))}>
                    Delete
                  </button>
                {/if}
              </div>
            </li>
          {/each}
        </ul>
      {/if}
    </div>
  {/if}
</section>

<style>
  .invites {
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
    padding: var(--space-4);
    border-radius: var(--radius-lg);
  }

  .invites__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  .invites h4 {
    margin: 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .invites__tools {
    display: flex;
    gap: var(--space-2);
    flex-wrap: wrap;
  }

  .invites__create {
    display: flex;
    gap: var(--space-3);
    flex-wrap: wrap;
  }

  .invites__create select {
    flex: 1 1 240px;
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-sm);
    border: var(--border-subtle);
    background: var(--color-surface-900);
    color: var(--color-text-primary);
    font-size: var(--font-size-sm);
  }

  .invites__group h5 {
    margin: 0 0 var(--space-2);
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  .invites ul {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }

  .invites__row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-2) var(--space-3);
    border-radius: var(--radius-md);
    border: var(--border-subtle);
    background: var(--color-surface-850);
    flex-wrap: wrap;
  }

  .invites__row--revoked {
    opacity: 0.55;
  }

  .invites__info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .invites__code {
    font-family: var(--font-mono, monospace);
    font-size: var(--font-size-sm);
    font-weight: 600;
    letter-spacing: 0.06em;
  }

  .invites__meta {
    font-size: var(--font-size-xs);
    color: var(--color-text-muted);
  }

  .invites__actions {
    display: flex;
    gap: var(--space-1);
    flex-wrap: wrap;
  }

  .invites__actions button {
    font-size: var(--font-size-xs);
    padding: var(--space-1) var(--space-2);
  }

  .invites__empty {
    margin: 0;
    font-size: var(--font-size-sm);
    color: var(--color-text-muted);
  }
</style>
